import { ArrowUpLeft, ArrowUpRight, Gauge, Activity, Timer, Zap, Trophy } from 'lucide-react';
import type { CornerData } from '@/types/telemetry';
import type { CornerMetric } from './CornerMetricTabs';
import { formatDynamicCornerLabel } from '@/lib/spline';
import { useI18n } from '@/context/I18nContext';

interface CornerStatsCardProps {
  corner: CornerData;
  activeMetric?: CornerMetric;
}

export function CornerStatsCard({ corner, activeMetric = 'speed' }: CornerStatsCardProps) {
  const { t } = useI18n();

  const isLeft = corner.direction === 'left';
  const isRight = corner.direction === 'right';

  const speedGain = corner.speedGainKmh ?? 0;
  const rpmGain = corner.rpmGain ?? 0;
  const durStats = corner.durationStats;

  const renderMetricIcon = () => {
    if (isLeft) return <ArrowUpLeft size={18} />;
    if (isRight) return <ArrowUpRight size={18} />;
    if (activeMetric === 'rpm') return <Activity size={18} />;
    if (activeMetric === 'duration') return <Timer size={18} />;
    return <Gauge size={18} />;
  };

  return (
    <div className="card p-5 space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-2 pb-3 border-b border-border-subtle">
        <div className="flex items-center gap-2.5">
          <div className="w-8 h-8 rounded-lg bg-accent/10 border border-accent/20 flex items-center justify-center text-accent">
            {renderMetricIcon()}
          </div>
          <div>
            <h2 className="text-base font-bold text-text-primary">
              {formatDynamicCornerLabel(corner.name, corner.orderIndex, t('corner.turn'))}
            </h2>
            <p className="text-xs text-text-muted">
              {isLeft ? t('corner.leftTurn') : isRight ? t('corner.rightTurn') : t('corner.turn')}
            </p>
          </div>
        </div>

        {/* Delta / Gain Badge */}
        {activeMetric === 'speed' && (
          <div className="flex items-center gap-2 bg-bg-surface border border-border-subtle px-3 py-1.5 rounded-lg">
            <Zap size={14} className={speedGain >= 0 ? 'text-green' : 'text-red'} />
            <span className="text-xs text-text-muted">{t('corner.speedGain')}:</span>
            <span className={`text-xs font-bold font-mono ${speedGain >= 0 ? 'text-green' : 'text-red'}`}>
              {speedGain >= 0 ? `+${speedGain.toFixed(1)}` : speedGain.toFixed(1)} km/h
            </span>
          </div>
        )}

        {activeMetric === 'rpm' && (
          <div className="flex items-center gap-2 bg-bg-surface border border-border-subtle px-3 py-1.5 rounded-lg">
            <Zap size={14} className={rpmGain >= 0 ? 'text-green' : 'text-red'} />
            <span className="text-xs text-text-muted">{t('corner.rpmGain')}:</span>
            <span className={`text-xs font-bold font-mono ${rpmGain >= 0 ? 'text-green' : 'text-red'}`}>
              {rpmGain >= 0 ? `+${Math.round(rpmGain)}` : Math.round(rpmGain)} RPM
            </span>
          </div>
        )}

        {activeMetric === 'duration' && durStats && (
          <div className="flex items-center gap-2 bg-bg-surface border border-border-subtle px-3 py-1.5 rounded-lg">
            <Timer size={14} className="text-accent" />
            <span className="text-xs text-text-muted">{t('corner.transitDuration')}:</span>
            <span className="text-xs font-bold font-mono text-accent">
              {(durStats.avgDurationMs / 1000).toFixed(3)} s
            </span>
          </div>
        )}
      </div>

      {/* Speed 3 Metric Cards */}
      {activeMetric === 'speed' && (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          {/* Entrance */}
          <div className="bg-bg-surface border border-border-subtle rounded-xl p-3.5 flex flex-col justify-between">
            <div>
              <div className="flex items-center justify-between mb-1">
                <span className="text-xs font-semibold text-green">{t('corner.entrance')}</span>
                <span className="text-[10px] px-1.5 py-0.5 rounded bg-green/10 text-green font-mono">
                  ±{(corner.entryStats?.stdDevKmh ?? 0).toFixed(1)}
                </span>
              </div>
              <div className="text-xl font-bold font-mono text-text-primary tracking-tight">
                {(corner.entryStats?.avgSpeedKmh ?? 0).toFixed(1)} <span className="text-xs text-text-muted font-normal">km/h</span>
              </div>
            </div>
            <div className="mt-3 pt-2 border-t border-border-subtle flex items-center justify-between text-[11px] text-text-muted font-mono">
              <span>{t('filter.min')}: {(corner.entryStats?.minSpeedKmh ?? 0).toFixed(1)}</span>
              <span className="text-green font-medium">{t('filter.max')}: {(corner.entryStats?.maxSpeedKmh ?? 0).toFixed(1)}</span>
            </div>
          </div>

          {/* Apex */}
          <div className="bg-bg-surface border border-border-subtle rounded-xl p-3.5 flex flex-col justify-between">
            <div>
              <div className="flex items-center justify-between mb-1">
                <span className="text-xs font-semibold text-red">{t('corner.apex')}</span>
                <span className="text-[10px] px-1.5 py-0.5 rounded bg-red/10 text-red font-mono">
                  ±{(corner.apexStats?.stdDevKmh ?? 0).toFixed(1)}
                </span>
              </div>
              <div className="text-xl font-bold font-mono text-text-primary tracking-tight">
                {(corner.apexStats?.avgSpeedKmh ?? 0).toFixed(1)} <span className="text-xs text-text-muted font-normal">km/h</span>
              </div>
            </div>
            <div className="mt-3 pt-2 border-t border-border-subtle flex items-center justify-between text-[11px] text-text-muted font-mono">
              <span>{t('filter.min')}: {(corner.apexStats?.minSpeedKmh ?? 0).toFixed(1)}</span>
              <span className="text-red font-medium">{t('filter.max')}: {(corner.apexStats?.maxSpeedKmh ?? 0).toFixed(1)}</span>
            </div>
          </div>

          {/* Exit */}
          <div className="bg-bg-surface border border-border-subtle rounded-xl p-3.5 flex flex-col justify-between">
            <div>
              <div className="flex items-center justify-between mb-1">
                <span className="text-xs font-semibold text-purple">{t('corner.exit')}</span>
                <span className="text-[10px] px-1.5 py-0.5 rounded bg-purple/10 text-purple font-mono">
                  ±{(corner.exitStats?.stdDevKmh ?? 0).toFixed(1)}
                </span>
              </div>
              <div className="text-xl font-bold font-mono text-text-primary tracking-tight">
                {(corner.exitStats?.avgSpeedKmh ?? 0).toFixed(1)} <span className="text-xs text-text-muted font-normal">km/h</span>
              </div>
            </div>
            <div className="mt-3 pt-2 border-t border-border-subtle flex items-center justify-between text-[11px] text-text-muted font-mono">
              <span>{t('filter.min')}: {(corner.exitStats?.minSpeedKmh ?? 0).toFixed(1)}</span>
              <span className="text-purple font-medium">{t('filter.max')}: {(corner.exitStats?.maxSpeedKmh ?? 0).toFixed(1)}</span>
            </div>
          </div>
        </div>
      )}

      {/* RPM 3 Metric Cards */}
      {activeMetric === 'rpm' && (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          {/* Entrance RPM */}
          <div className="bg-bg-surface border border-border-subtle rounded-xl p-3.5 flex flex-col justify-between">
            <div>
              <div className="flex items-center justify-between mb-1">
                <span className="text-xs font-semibold text-green">{t('corner.entrance')}</span>
                <span className="text-[10px] px-1.5 py-0.5 rounded bg-green/10 text-green font-mono">
                  ±{Math.round(corner.entryRpmStats?.stdDevRpm ?? 0)}
                </span>
              </div>
              <div className="text-xl font-bold font-mono text-text-primary tracking-tight">
                {Math.round(corner.entryRpmStats?.avgRpm ?? 0).toLocaleString()} <span className="text-xs text-text-muted font-normal">RPM</span>
              </div>
            </div>
            <div className="mt-3 pt-2 border-t border-border-subtle flex items-center justify-between text-[11px] text-text-muted font-mono">
              <span>{t('filter.min')}: {Math.round(corner.entryRpmStats?.minRpm ?? 0).toLocaleString()}</span>
              <span className="text-green font-medium">{t('filter.max')}: {Math.round(corner.entryRpmStats?.maxRpm ?? 0).toLocaleString()}</span>
            </div>
          </div>

          {/* Apex RPM */}
          <div className="bg-bg-surface border border-border-subtle rounded-xl p-3.5 flex flex-col justify-between">
            <div>
              <div className="flex items-center justify-between mb-1">
                <span className="text-xs font-semibold text-red">{t('corner.apex')}</span>
                <span className="text-[10px] px-1.5 py-0.5 rounded bg-red/10 text-red font-mono">
                  ±{Math.round(corner.apexRpmStats?.stdDevRpm ?? 0)}
                </span>
              </div>
              <div className="text-xl font-bold font-mono text-text-primary tracking-tight">
                {Math.round(corner.apexRpmStats?.avgRpm ?? 0).toLocaleString()} <span className="text-xs text-text-muted font-normal">RPM</span>
              </div>
            </div>
            <div className="mt-3 pt-2 border-t border-border-subtle flex items-center justify-between text-[11px] text-text-muted font-mono">
              <span>{t('filter.min')}: {Math.round(corner.apexRpmStats?.minRpm ?? 0).toLocaleString()}</span>
              <span className="text-red font-medium">{t('filter.max')}: {Math.round(corner.apexRpmStats?.maxRpm ?? 0).toLocaleString()}</span>
            </div>
          </div>

          {/* Exit RPM */}
          <div className="bg-bg-surface border border-border-subtle rounded-xl p-3.5 flex flex-col justify-between">
            <div>
              <div className="flex items-center justify-between mb-1">
                <span className="text-xs font-semibold text-purple">{t('corner.exit')}</span>
                <span className="text-[10px] px-1.5 py-0.5 rounded bg-purple/10 text-purple font-mono">
                  ±{Math.round(corner.exitRpmStats?.stdDevRpm ?? 0)}
                </span>
              </div>
              <div className="text-xl font-bold font-mono text-text-primary tracking-tight">
                {Math.round(corner.exitRpmStats?.avgRpm ?? 0).toLocaleString()} <span className="text-xs text-text-muted font-normal">RPM</span>
              </div>
            </div>
            <div className="mt-3 pt-2 border-t border-border-subtle flex items-center justify-between text-[11px] text-text-muted font-mono">
              <span>{t('filter.min')}: {Math.round(corner.exitRpmStats?.minRpm ?? 0).toLocaleString()}</span>
              <span className="text-purple font-medium">{t('filter.max')}: {Math.round(corner.exitRpmStats?.maxRpm ?? 0).toLocaleString()}</span>
            </div>
          </div>
        </div>
      )}

      {/* Duration 3 Metric Cards */}
      {activeMetric === 'duration' && (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          {/* Best Duration */}
          <div className="bg-bg-surface border border-border-subtle rounded-xl p-3.5 flex flex-col justify-between">
            <div>
              <div className="flex items-center justify-between mb-1">
                <span className="text-xs font-semibold text-green flex items-center gap-1">
                  <Trophy size={12} /> {t('corner.bestDuration')}
                </span>
              </div>
              <div className="text-xl font-bold font-mono text-text-primary tracking-tight">
                {durStats ? (durStats.minDurationMs / 1000).toFixed(3) : '—'} <span className="text-xs text-text-muted font-normal">s</span>
              </div>
            </div>
            <div className="mt-3 pt-2 border-t border-border-subtle flex items-center justify-between text-[11px] text-text-muted font-mono">
              <span>{t('corner.fastest')}</span>
              <span className="text-green font-medium">#1</span>
            </div>
          </div>

          {/* Average Duration */}
          <div className="bg-bg-surface border border-border-subtle rounded-xl p-3.5 flex flex-col justify-between">
            <div>
              <div className="flex items-center justify-between mb-1">
                <span className="text-xs font-semibold text-accent">{t('corner.avgDuration')}</span>
                {durStats && (
                  <span className="text-[10px] px-1.5 py-0.5 rounded bg-accent/10 text-accent font-mono">
                    ±{(durStats.stdDevMs / 1000).toFixed(3)}s
                  </span>
                )}
              </div>
              <div className="text-xl font-bold font-mono text-text-primary tracking-tight">
                {durStats ? (durStats.avgDurationMs / 1000).toFixed(3) : '—'} <span className="text-xs text-text-muted font-normal">s</span>
              </div>
            </div>
            <div className="mt-3 pt-2 border-t border-border-subtle flex items-center justify-between text-[11px] text-text-muted font-mono">
              <span>{t('sectorPanel.median')}: {durStats ? (durStats.medianDurationMs / 1000).toFixed(3) : '—'}s</span>
            </div>
          </div>

          {/* Slowest Duration */}
          <div className="bg-bg-surface border border-border-subtle rounded-xl p-3.5 flex flex-col justify-between">
            <div>
              <div className="flex items-center justify-between mb-1">
                <span className="text-xs font-semibold text-text-muted">{t('corner.worstDuration')}</span>
              </div>
              <div className="text-xl font-bold font-mono text-text-primary tracking-tight">
                {durStats ? (durStats.maxDurationMs / 1000).toFixed(3) : '—'} <span className="text-xs text-text-muted font-normal">s</span>
              </div>
            </div>
            <div className="mt-3 pt-2 border-t border-border-subtle flex items-center justify-between text-[11px] text-text-muted font-mono">
              <span>{t('corner.spread')}: {durStats ? ((durStats.maxDurationMs - durStats.minDurationMs) / 1000).toFixed(3) : '—'}s</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
